import React, { useState } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/InputDefault";
import FormFieldComponent from "./field/FormFieldMain";
import { FormField, FormSection } from "../types";
import IconProvider from "@/components/ui/IconProvider";
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { RoleVisibilitySelector, getVisibilityState } from "@/components/ui/RoleVisibilitySelector";

interface FormSectionProps {
  section: FormSection;
  sectionIndex: number;
  onAddField: (sectionId: string) => void;
  onRemoveSection: (sectionId: string) => void;
  onUpdateField: (
    sectionId: string,
    fieldId: string,
    updates: Partial<FormField>
  ) => void;
  onUpdateSectionTitle: (sectionId: string, title: string) => void;
  onRemoveField: (sectionId: string, fieldId: string) => void;
  onFieldDragEnd: () => void;
  dragHandleProps?: {
    attributes?: any;
    listeners?: any;
  };
  lastAddedFieldId?: string;
  onClearLastAddedFieldId?: () => void;
  disabled?: boolean;
  showDocumentation?: boolean;
  onUpdate?: (sectionId: string, section: FormSection) => void;
  totalSections?: number;
  previewMode?: boolean;
  previewRole?: string | null;
  onSectionVisibilityChange?: (sectionId: string, roleId: string, state: 'visible-editable' | 'visible-readonly' | 'hidden') => void;
  onFieldVisibilityChange?: (sectionId: string, fieldId: string, roleId: string, state: 'visible-editable' | 'visible-readonly' | 'hidden') => void;
}

export default function FormSectionComponent({
  section,
  sectionIndex,
  onAddField,
  onRemoveSection,
  onUpdateField,
  onUpdateSectionTitle,
  onRemoveField,
  onFieldDragEnd,
  dragHandleProps,
  lastAddedFieldId,
  onClearLastAddedFieldId,
  disabled = false,
  showDocumentation = false,
  onUpdate,
  totalSections = 1,
  previewMode = false,
  previewRole = null,
  onSectionVisibilityChange,
  onFieldVisibilityChange,
}: FormSectionProps) {
  const { t } = usePlatformTranslation();
  const [titleValue, setTitleValue] = useState(section.title);
  const [showDocEditor, setShowDocEditor] = useState(false);

  // Filter fields based on role visibility and mark readonly fields
  const visibleFields = React.useMemo(() => {
    if (!previewMode || !previewRole) {
      return section.fields;
    }
    
    const filtered = section.fields
      .filter(field => {
        // Hide field if it's hidden for the preview role
        if (field.hiddenForRoles?.includes(previewRole)) {
          return false;
        }
        return true;
      })
      .map(field => {
        const isReadOnly = field.readOnlyForRoles?.includes(previewRole) || 
                          section.readOnlyForRoles?.includes(previewRole) || false;
        
        return {
          ...field,
          isReadOnly,
        };
      });
    
    return filtered;
  }, [section.fields, previewMode, previewRole, section.readOnlyForRoles, section.id]);

  // Handle title change (update local state only)
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setTitleValue(newValue);
  };

  // Save title changes when focus is lost
  const handleTitleBlur = () => {
    if (titleValue !== section.title) {
      onUpdateSectionTitle(section.id, titleValue);
    }
  };

  // Handle documentation changes
  const handleDocumentationChange = (value: string) => {
    if (onUpdate) {
      onUpdate(section.id, { ...section, documentation: value });
    }
  };

  return (
    <div className="border border-border rounded-sm p-4 bg-muted">
      {/* Section Header */}
      <div className="flex items-center gap-3 mb-4">
        {!disabled && dragHandleProps && (
          <div
            {...dragHandleProps.attributes}
            {...dragHandleProps.listeners}
            className="cursor-grab active:cursor-grabbing p-1 text-muted-foreground hover:text-foreground flex items-center"
          >
            <IconProvider iconstring="fa-grip-dots-vertical" size={16} />
          </div>
        )}
        <div className="flex-1 items-center">
          {disabled ? (
            <h3 className="text-lg font-semibold text-foreground">{titleValue}</h3>
          ) : (
            <Input
              value={titleValue}
              onChange={handleTitleChange}
              onBlur={handleTitleBlur}
              className="heading-6-compact mb-0 border-0 bg-transparent p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              placeholder={t('platform.wizards.blueprints.section_title')}
              disabled={disabled}
            />
          )}
        </div>

        {/* Role Visibility Selector - Show when a role is selected */}
        {previewRole && onSectionVisibilityChange && (
          <RoleVisibilitySelector
            state={getVisibilityState(
              previewRole,
              section.hiddenForRoles,
              section.readOnlyForRoles
            )}
            onStateChange={(newState) => {
              onSectionVisibilityChange(section.id, previewRole, newState);
            }}
            disabled={false}
            size="sm"
          />
        )}
        
        {!disabled && (
          <Button
            variant="halfghost"
            size="sm"
            onClick={() => onRemoveSection(section.id)}
            className="mr-3 text-xs w-9 h-9"
            tooltip={totalSections <= 1 ? "Cannot delete the last section" : "Remove this section"}
            disabled={totalSections <= 1}
          >
            <IconProvider
              iconstring="fa-xmark"
              size={16}
              color="currentColor"
              className="hover:opacity-80 transition-colors"
            />
          </Button>
        )}
      </div>

      {/* Documentation Editor Toggle - Only in edit mode */}
      {!disabled && (
        <div className="mb-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowDocEditor(!showDocEditor)}
            className={`text-xs h-7 px-2 ${section.documentation ? 'text-purple-1 hover:text-purple-800' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <IconProvider 
              iconstring={showDocEditor ? "fa-chevron-up" : "fa-book"} 
              size={12} 
              className="mr-1.5" 
            />
            {showDocEditor ? "Hide" : (section.documentation ? "Edit" : "Add")} Documentation
            {section.documentation && !showDocEditor && (
              <span className="ml-1.5 px-1.5 py-0.5 bg-purple-1/20 text-purple-1 rounded text-[10px] font-medium">
                Active
              </span>
            )}
          </Button>
        </div>
      )}

      {/* Documentation Editor */}
      {!disabled && showDocEditor && (
        <div className="mb-4">
          <textarea
            className="w-full p-3 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y"
            style={{ minHeight: '60px', maxHeight: '200px' }}
            placeholder={t('platform.wizards.blueprints.add_documentation_for_this_section')}
            defaultValue={section.documentation || ''}
            onChange={(e) => handleDocumentationChange(e.target.value)}
          />
        </div>
      )}

      {/* Documentation Display - When toggle is on */}
      {showDocumentation && (
        <div className={`mb-4 p-3 rounded ${section.documentation ? 'bg-blue-500/10 border-l-4 border-blue-500 dark:bg-blue-500/20' : 'bg-muted border-l-4 border-border'}`}>
          <div className="flex items-start gap-2">
            <IconProvider 
              iconstring={section.documentation ? "fa-circle-info" : "fa-circle-exclamation"} 
              size={14} 
              className={`mt-0.5 ${section.documentation ? 'text-blue-600 dark:text-blue-400' : 'text-muted-foreground'}`} 
            />
            {section.documentation ? (
              <div 
                className="text-sm text-foreground flex-1"
                dangerouslySetInnerHTML={{ __html: section.documentation }}
              />
            ) : (
              <div className="text-sm text-muted-foreground italic flex-1">
                No documentation available
              </div>
            )}
          </div>
        </div>
      )}

      {/* Fields */}
      <SortableContext
        items={visibleFields.map(field => field.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-2">
          {visibleFields.map((field) => (
            <FormFieldComponent
              key={field.id}
              field={field}
              sectionId={section.id}
              onUpdate={onUpdateField}
              onRemove={onRemoveField}
              isNewField={field.id === lastAddedFieldId}
              onClearNewField={onClearLastAddedFieldId}
              disabled={disabled}
              showDocumentation={showDocumentation}
              previewMode={previewMode}
              previewRole={previewRole}
              onFieldVisibilityChange={onFieldVisibilityChange}
            />
          ))}
        </div>
      </SortableContext>

      {/* Add Field Button - hide in view mode */}
      {!disabled && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onAddField(section.id)}
          className="mt-3 w-full bg-card border-dashed border-border text-muted-foreground hover:text-foreground"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Field
        </Button>
      )}
    </div>
  );
}
